import { action, makeObservable, observable } from 'mobx';

export type SourceSortOrder = 'newest-first' | 'oldest-first';
export type SortDirection = 'ASC' | 'DESC';

export interface SortColumn {
  readonly columnKey: string;
  readonly direction: SortDirection;
}

const STORAGE_KEY = 'cq-source-sort-preferences';
const COLUMN_SORT_STORAGE_KEY = 'cq-column-sort-preferences';

export class UiSourceSortStore {
  // Track ULID-based sort order preference per source ID (legacy)
  sortOrderById: Record<string, SourceSortOrder> = {};

  // Track column-based sorts per source ID (new: Airtable-style)
  columnSortsBySourceId: Record<string, SortColumn[]> = {};

  constructor() {
    makeObservable(this, {
      sortOrderById: observable,
      columnSortsBySourceId: observable,
      setSortOrder: action.bound,
      toggleSortOrder: action.bound,
      setColumnSorts: action.bound,
    });

    // Load preferences from sessionStorage on initialization
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.sortOrderById = parsed;
      }
    } catch (error) {
      console.warn('Failed to load sort preferences from sessionStorage:', error);
    }

    try {
      const columnStored = sessionStorage.getItem(COLUMN_SORT_STORAGE_KEY);
      if (columnStored) {
        const parsed = JSON.parse(columnStored);
        this.columnSortsBySourceId = parsed;
      }
    } catch (error) {
      console.warn('Failed to load column sort preferences from sessionStorage:', error);
    }
  }

  private saveToStorage() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.sortOrderById));
    } catch (error) {
      console.warn('Failed to save sort preferences to sessionStorage:', error);
    }
  }

  private saveColumnSortsToStorage() {
    try {
      sessionStorage.setItem(COLUMN_SORT_STORAGE_KEY, JSON.stringify(this.columnSortsBySourceId));
    } catch (error) {
      console.warn('Failed to save column sort preferences to sessionStorage:', error);
    }
  }

  // Legacy ULID-based sort methods
  getSortOrder(sourceId: string): SourceSortOrder {
    return this.sortOrderById[sourceId] || 'oldest-first';
  }

  setSortOrder(sourceId: string, sortOrder: SourceSortOrder) {
    this.sortOrderById[sourceId] = sortOrder;
    this.saveToStorage();
  }

  toggleSortOrder(sourceId: string) {
    const currentOrder = this.getSortOrder(sourceId);
    const newOrder: SourceSortOrder =
      currentOrder === 'newest-first' ? 'oldest-first' : 'newest-first';
    this.setSortOrder(sourceId, newOrder);
  }

  isNewestFirst(sourceId: string): boolean {
    return this.getSortOrder(sourceId) === 'newest-first';
  }

  // Column-based sort methods (Airtable-style)
  getColumnSorts(sourceId: string): SortColumn[] {
    return this.columnSortsBySourceId[sourceId] || [];
  }

  setColumnSorts(sourceId: string, sorts: SortColumn[]) {
    this.columnSortsBySourceId[sourceId] = sorts;
    this.saveColumnSortsToStorage();
  }

  hasColumnSorts(sourceId: string): boolean {
    return (this.columnSortsBySourceId[sourceId]?.length || 0) > 0;
  }

  clearColumnSorts(sourceId: string) {
    delete this.columnSortsBySourceId[sourceId];
    this.saveColumnSortsToStorage();
  }
}
